import React from "react";
import { CheckCircle2 } from "lucide-react";
import { Modal, VbButton } from "@/components/vb";
import type { BillingLockReason } from "@/types/billing";

interface CancellationDialogProps {
  lockReason: BillingLockReason | null;
  error: string | null;
  submitting: boolean;
  onReactivate: () => void;
}

const TRIAL_FEATURES = [
  "Your Trust Signal score and report",
  "Your fix list, in the right order",
  "Your workspace and company history",
  "Your Virtual Buyer research",
];

const SUBSCRIPTION_FEATURES = [
  "Your score and reporting history",
  "Your fix list, including finished work",
  "Your workspace and company history",
  "Your Virtual Buyer research",
];

export const CancellationDialog: React.FC<CancellationDialogProps> = ({
  lockReason,
  error,
  submitting,
  onReactivate,
}) => {
  const trialEnded = lockReason === "trial_ended";
  const features = trialEnded ? TRIAL_FEATURES : SUBSCRIPTION_FEATURES;

  return (
    <Modal
      open
      onOpenChange={() => undefined}
      title={trialEnded ? "Your trial has ended" : "Your subscription has ended"}
      description={
        trialEnded
          ? "You cancelled, so your card was never charged. Reactivate to pick up where you left off."
          : "Nothing was deleted. One click turns it back on."
      }
      widthClassName="max-w-xl"
      showCloseButton={false}
      onInteractOutside={(event) => event.preventDefault()}
      onEscapeKeyDown={(event) => event.preventDefault()}
      footer={
        <div className="w-full space-y-3">
          {error ? (
            <p
              className="rounded-vb-sm bg-vb-fail-bg px-3 py-2 text-sm font-medium text-vb-fail-ink"
              role="alert"
            >
              {error}
            </p>
          ) : null}
          <VbButton
            block
            size="lg"
            disabled={submitting}
            onClick={onReactivate}
          >
            {submitting ? "Reactivating..." : "Reactivate My Account"}
          </VbButton>
          <p className="text-center text-xs font-medium leading-relaxed text-vb-text-3">
            Charges the card on file with Stripe $25 today, then monthly. Cancel anytime.
          </p>
        </div>
      }
    >
      <div className="space-y-3 py-2">
        <p className="text-xs font-bold uppercase text-vb-text-3">
          {trialEnded ? "Waiting for you" : "Still here"}
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          {features.map((feature) => (
            <div key={feature} className="flex items-start gap-2 text-sm font-medium text-vb-text">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-vb-pass" />
              <span>{feature}</span>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};

export default CancellationDialog;